import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useState } from "react";

import {
  Badge,
  HeadsetImage,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { useNotificationContext } from "@contexts/notification-context";
import { trackSimPassCreated } from "@lib/event-trackers";
import { useTimer } from "@lib/hooks";
import { useCurrentUser } from "@queries";
import { filterUserLoginCodesByLEId } from "@services/account.utils";
import { useContainer } from "@src/contexts/container-context";
import { ContentType } from "@transfr-inc/dashboard-components/curriculum";
import { ActivityStatusNotification } from "@views/my-work/components/course/activity-status-notification";

import "./ih-activity-view.scss";

// TEMPORARY (ENG-21934): sim thumbnails are turned off, so the IH activity view
// renders HeadsetImage's built-in placeholder for every activity. The resolution
// plumbing below — XR_ASSETS_CDN_BASE_URL, the vite.config.js whitelist entry, and
// the thumbnail_image_path pass-down in index.jsx — is deliberately left intact.
// Flip this back to true to restore the feature; nothing else needs to change.
const THUMBNAILS_ENABLED = false;

const IHActivityView = ({
  title,
  courseId,
  description,
  timeToComplete,
  masteryStatus = "", // This will be used for the notification component
  timeSpent,
  completionDate,
  latestTimestamp,
  masteryScore,
  learningExperienceId,
  appName,
  categoryCode,
  classroomId,
  moduleId,
  thumbnail_image_path,
}) => {
  const timer = useTimer();

  // The API returns a bucket-relative path; the CDN host is per-environment.
  // Left undefined when there is no path so HeadsetImage renders its fallback
  // without first issuing a request that is certain to 404.
  const cdnBaseUrl = process.env.XR_ASSETS_CDN_BASE_URL?.replace(/\/$/, "");
  const thumbnailUrl =
    THUMBNAILS_ENABLED && thumbnail_image_path
      ? `${cdnBaseUrl}/${thumbnail_image_path}`
      : undefined;

  const { accountService } = useContainer();

  const [showInstructions, setShowInstructions] = useState(true);
  const [generatingSimPass, setGeneratingSimPass] = useState();
  const [simPass, setSimPass] = useState();
  const currentUser = useCurrentUser();
  const { userId } = currentUser || {};

  const { addNotification } = useNotificationContext();

  useEffect(() => {
    // Retrieve and display any existing, active sim pass for this activity so a
    // reload doesn't hide a code that is still valid in the headset.
    if (!userId || !classroomId || !learningExperienceId) {
      return;
    }

    let cancelled = false;

    accountService
      .fetchMostRecentCodes({
        user_id: userId,
        classroom_id: classroomId,
        simulation_id: learningExperienceId,
      })
      .then((codes) => {
        if (cancelled) {
          return;
        }
        const existing = filterUserLoginCodesByLEId(
          codes,
          userId,
          learningExperienceId
        );
        if (existing) {
          timer.setTimer(existing.ttl * 60);
          setSimPass(existing.code);
        }
      })
      .catch(() => {
        // Silently fail - sim pass may not exist yet
      });

    return () => {
      cancelled = true;
    };
  }, [userId, classroomId, learningExperienceId]);

  const generateClassroomLoginCode = async () => {
    setGeneratingSimPass(true);
    try {
      const payload = {
        userIds: [userId],
        classroomId: classroomId,
        simulationId: learningExperienceId,
        courseId: courseId,
        categoryCode: categoryCode,
        moduleId: moduleId,
      };

      const [result] = await accountService.createLoginCodes(payload);
      timer.setTimer((result?.ttl ?? 0) * 60);
      setSimPass(result?.code);

      await trackSimPassCreated(
        learningExperienceId,
        title,
        classroomId,
        Product.PROG
      );
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Something went wrong. Please try again.",
      });
    }
    setGeneratingSimPass(false);
  };

  useEffect(() => {
    if (timer.isDone) {
      setSimPass();
    }
  }, [timer.isDone]);

  return (
    <div className="ih-main-container">
      <div className="ih-content-container">
        <div className="ih-left-container">
          <ActivityStatusNotification
            status={masteryStatus}
            contentType={ContentType.InHeadset.id}
            masteryScore={masteryScore}
            completionDate={completionDate}
            latestTimestamp={latestTimestamp}
            timeSpent={timeSpent}
            timeToComplete={timeToComplete}
            learningExperienceId={learningExperienceId}
          />
          <div className="activity-title">{title}</div>
          <div className="activity-description">{description}</div>
          <div
            className="help-container"
            onClick={() => setShowInstructions(!showInstructions)}
          >
            <FontAwesomeIcon
              icon="fa-solid fa-caret-right"
              rotation={showInstructions ? 90 : 0}
              className="chevron-icon"
            />
            <div className="help-text">How do I find this in my headset?</div>
          </div>
          {
            <div className={clsx("instructions", showInstructions && "show")}>
              <div className="instructions-container">
                <div className="instructions-badge">
                  <Badge className="badge" value={1} />
                </div>
                <div className="instructions-text-container">
                  <div className="text-title">Generate Sim Pass</div>
                  <div className="text-description">
                    Select the Get Sim Pass button on this screen to generate a
                    unique code. Write this code down or memorize it. Note which
                    application you need to use (such as Virtual Training
                    Facility).
                  </div>
                </div>
              </div>
              <div className="instructions-container">
                <div className="instructions-badge">
                  <Badge className="badge" value={2} />
                </div>
                <div className="instructions-text-container">
                  <div className="text-title">Grab VR headset</div>
                  <div className="text-description">
                    Start up VR headset. See{" "}
                    <span
                      onClick={() => console.log("navigate to startup guide")}
                    >
                      <span className="startup">startup guide</span>{" "}
                      <FontAwesomeIcon
                        icon="fa-solid fa-arrow-up-right-from-square"
                        rotation={0}
                        className="box-icon"
                      />{" "}
                    </span>
                    if you need further assistance.
                  </div>
                </div>
              </div>
              <div className="instructions-container">
                <div className="instructions-badge">
                  <Badge className="badge" value={3} />
                </div>
                <div className="instructions-text-container">
                  <div className="text-title">Select the application</div>
                  <div className="text-description">
                    Select the app where the sim is located, such as Virtual
                    Training Facility.
                  </div>
                </div>
              </div>
              <div className="instructions-container">
                <div className="instructions-badge">
                  <Badge className="badge" value={4} />
                </div>
                <div className="instructions-text-container">
                  <div className="text-title">
                    Enter Sim Pass in the headset
                  </div>
                  <div className="text-description">
                    Enter the Sim Pass code you generated on the login screen.
                    This should lead you directly to the menu you need, where
                    the simulation will be highlighted.
                  </div>
                </div>
              </div>
              <div className="instructions-container">
                <div className="instructions-badge">
                  <Badge className="badge" value={5} />
                </div>
                <div className="instructions-text-container">
                  <div className="text-title">Play the sim</div>
                  <div className="text-description">
                    Learn and practice new skills in the simulation. The results
                    will be recorded on this page automatically.
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
        <div className="ih-right-container">
          <HeadsetImage image_url={thumbnailUrl} />
          {`App: ${appName}`}
          <div className="ih-button-container">
            {simPass ? (
              <Button
                disabled={generatingSimPass}
                className="sim-code-button"
                onClick={generateClassroomLoginCode}
              >
                {simPass}{" "}
                <span className="time">
                  <FontAwesomeIcon
                    icon="fa-solid fa-clock"
                    rotation={0}
                    className="box-icon"
                  />
                  {timer.timeFormatted}
                </span>
              </Button>
            ) : (
              <Button
                disabled={generatingSimPass}
                icon={"fa-regular fa-ticket"}
                className="sim-pass-button"
                onClick={generateClassroomLoginCode}
              >
                Get Sim Pass
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IHActivityView;
